import { Box, Stack } from '@mui/material'
import type { ReactNode } from 'react'

import { BodyText } from '@/components/atoms/BodyText'
import { MetaText } from '@/components/atoms/MetaText'

import type { AuditHit, GuidanceCheckPoint } from '../../types'
import { GuidanceCheckPointCard } from '../GuidanceCheckPointCard'
import { GuidancePrepRow } from '../GuidancePrepRow'
import { EmptyLine, InlineTag } from '../GuidancePrepCard/parts'

interface Props {
  // null = 作っていない (β でない・生成に失敗)
  hits: AuditHit[] | null
  // 確認要点のうち禁忌該当 (is_contraindicated) のもの. 服薬指導の面では ● を出さず、
  // ここに寄せる (yakureki-back log/262 work-log/08 §5).
  contraindicatedPoints: GuidanceCheckPoint[]
}

// 禁忌の ● は「属性があって禁忌に当たる」ときだけ. 未取得 × 禁忌は「聞く」行なので ・ のまま.
// 色は確認要点の禁忌 marker と同じ state.dangerFg (= palette error.main).
const HitRow = ({ hit }: { hit: AuditHit }) => {
  const isContraindicated =
    hit.relation === '禁忌' && hit.profileState === 'あり'
  const quote = hit.insertQuote ? `・添付文書「${hit.insertQuote}」` : ''
  return (
    <GuidancePrepRow
      marker={isContraindicated ? '●' : '・'}
      markerColor={isContraindicated ? 'error.main' : 'text.disabled'}
      main={
        <BodyText>
          {hit.drug} — {hit.statement}
          <InlineTag>{hit.relation}</InlineTag>
        </BodyText>
      }
      sub={`${hit.attribute}: ${hit.profileState}${quote}`}
    />
  )
}

const HitGroup = ({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) => (
  <Box role="group" aria-label={label}>
    <MetaText sx={{ mb: 0.5 }}>{label}</MetaText>
    <Stack spacing={1}>{children}</Stack>
  </Box>
)

// 監査の面 1「監査の要点」. 2 群に分ける (群の語は yakureki-back log/262 決定 15):
//   該当         (profileState あり + 禁忌該当の確認要点)
//   属性が未取得 (profileState 未取得. 投薬で聞く)
export const AuditHitList = ({ hits, contraindicatedPoints }: Props) => {
  const confirm = (hits ?? []).filter((h) => h.profileState === 'あり')
  const ask = (hits ?? []).filter((h) => h.profileState === '未取得')
  const hasConfirm = confirm.length > 0 || contraindicatedPoints.length > 0

  if (!hasConfirm && ask.length === 0) {
    return (
      <EmptyLine>
        {hits === null ? '(まだ作られていません)' : '(なし)'}
      </EmptyLine>
    )
  }

  return (
    <Stack spacing={1.5}>
      {hasConfirm && (
        <HitGroup label="該当">
          {confirm.map((h, i) => (
            <HitRow key={`confirm-${i}`} hit={h} />
          ))}
          {contraindicatedPoints.map((p, i) => (
            <GuidanceCheckPointCard key={`point-${i}`} point={p} />
          ))}
        </HitGroup>
      )}
      {ask.length > 0 && (
        <HitGroup label="属性が未取得">
          {ask.map((h, i) => (
            <HitRow key={`ask-${i}`} hit={h} />
          ))}
        </HitGroup>
      )}
    </Stack>
  )
}
